/**
 * Tipos y interfaces para el sistema de solicitud de albaranes
 */

// Estados posibles de una solicitud de albarán
export const ALBARAN_STATUS = {
  PENDIENTE: 'pendiente', // Solicitud recibida desde API (Kanban) pendiente de gestionar
  ENVIADA: 'enviada',     // Solicitud creada desde la app interna
  PROCESADA: 'procesada', // Albarán creado y enviado para firma
  ENTREGADA: 'entregada'  // Albarán firmado y completado
};

// Campos requeridos para una solicitud de albarán
export const REQUIRED_FIELDS = [
  'numeroPedido',
  'tecnicoResponsable',
  'nombreDestinatario',
  'numeroSerie',
  'consignatario',
  'direccionEntrega',
  'personaContacto',
  'horarioRecepcion'
];

// Campos opcionales
export const OPTIONAL_FIELDS = [
  'numeroPresupuesto',
  'telefonoContacto',
  'tipoMaterial',
  'cantidad',
  'observaciones',
  'condicionesEspeciales'
];

/**
 * Estructura de datos para una solicitud de albarán
 * @typedef {Object} AlbaranRequest
 * @property {string} id - UUID generado automáticamente
 * @property {string} requesterId - UID del técnico que solicita
 * @property {string} requesterName - Nombre del técnico
 * @property {string} requesterEmail - Email del técnico
 * @property {string} companyId - ID de la empresa (multi-tenant)
 * 
 * // Datos del albarán
 * @property {string} numeroPedido - Número de pedido
 * @property {string} tecnicoResponsable - Técnico responsable
 * @property {string} nombreDestinatario - Nombre del destinatario/cliente
 * @property {string} numeroSerie - Número de serie del equipo/material
 * @property {string} numeroPresupuesto - Número de presupuesto (opcional)
 * @property {string} consignatario - Empresa receptora
 * @property {string} direccionEntrega - Dirección exacta de entrega
 * @property {string} personaContacto - Persona de contacto
 * @property {string} telefonoContacto - Teléfono de contacto (opcional)
 * @property {string} horarioRecepcion - Horario de recepción de mercancías
 * 
 * // Detalles adicionales
 * @property {string} tipoMaterial - Tipo de material o equipo (opcional)
 * @property {string} cantidad - Cantidad de elementos (opcional)
 * @property {string} observaciones - Observaciones o comentarios adicionales (opcional)
 * @property {string} condicionesEspeciales - Condiciones especiales de entrega (opcional)
 * 
 * // Metadatos
 * @property {string} status - Estado de la solicitud
 * @property {Date} createdAt - Fecha de creación
 * @property {Date} updatedAt - Fecha de última actualización
 * @property {Date} emailSentAt - Fecha de envío del email
 * @property {Date} processedAt - Fecha de procesamiento
 */

/**
 * Crea una nueva solicitud de albarán con valores por defecto
 * @param {Object} userData - Datos del usuario autenticado
 * @param {Object} formData - Datos del formulario
 * @returns {AlbaranRequest} Nueva solicitud de albarán
 */
export const createAlbaranRequest = (userData, formData) => {
  const now = new Date();
  
  return {
    id: '', // Se generará automáticamente en Firestore
    requesterId: userData.uid,
    requesterName: userData.displayName || userData.email,
    requesterEmail: userData.email,
    companyId: userData.companyId,
    
    // Datos del albarán
    numeroPedido: formData.numeroPedido || '',
    tecnicoResponsable: formData.tecnicoResponsable || userData.displayName || '',
    nombreDestinatario: formData.nombreDestinatario || '',
    numeroSerie: formData.numeroSerie || '',
    numeroPresupuesto: formData.numeroPresupuesto || '',
    consignatario: formData.consignatario || '',
    direccionEntrega: formData.direccionEntrega || '',
    personaContacto: formData.personaContacto || '',
    telefonoContacto: formData.telefonoContacto || '',
    horarioRecepcion: formData.horarioRecepcion || '',
    
    // Detalles adicionales
    tipoMaterial: formData.tipoMaterial || '',
    cantidad: formData.cantidad || '',
    observaciones: formData.observaciones || '',
    condicionesEspeciales: formData.condicionesEspeciales || '',
    
    // Metadatos
    status: ALBARAN_STATUS.ENVIADA,
    createdAt: now,
    updatedAt: now,
    emailSentAt: null,
    processedAt: null
  };
};

/**
 * Valida que una solicitud de albarán tenga todos los campos requeridos
 * @param {AlbaranRequest} request - Solicitud a validar
 * @returns {Object} Resultado de la validación
 */
export const validateAlbaranRequest = (request) => {
  const errors = {};
  const missingFields = [];
  
  // Validar campos requeridos
  REQUIRED_FIELDS.forEach(field => {
    if (!request[field] || request[field].trim() === '') {
      missingFields.push(field);
      errors[field] = 'Este campo es obligatorio';
    }
  });
  
  // Validar formato de email si está presente
  if (request.requesterEmail && !isValidEmail(request.requesterEmail)) {
    errors.requesterEmail = 'Formato de email inválido';
  }
  
  // Validar formato de teléfono si está presente
  if (request.telefonoContacto && !isValidPhoneNumber(request.telefonoContacto)) {
    errors.telefonoContacto = 'Formato de teléfono inválido';
  }
  
  return {
    isValid: missingFields.length === 0 && Object.keys(errors).length === 0,
    errors,
    missingFields
  };
};

/**
 * Valida formato de email
 * @param {string} email - Email a validar
 * @returns {boolean} True si es válido
 */
const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Valida formato de teléfono español
 * @param {string} phone - Teléfono a validar
 * @returns {boolean} True si es válido
 */
const isValidPhoneNumber = (phone) => {
  // Formato español: 9 dígitos, puede empezar con +34
  const phoneRegex = /^(\+34)?[6-9]\d{8}$/;
  return phoneRegex.test(phone.replace(/\s/g, ''));
};

/**
 * Transforma los datos del formulario para el envío
 * @param {Object} formData - Datos del formulario
 * @returns {Object} Datos transformados
 */
export const transformFormData = (formData) => {
  const transformed = { ...formData };
  
  // Limpiar espacios en blanco
  Object.keys(transformed).forEach(key => {
    if (typeof transformed[key] === 'string') {
      transformed[key] = transformed[key].trim();
    }
  });
  
  // Formatear teléfono si está presente
  if (transformed.telefonoContacto) {
    transformed.telefonoContacto = transformed.telefonoContacto.replace(/\s/g, '');
  }
  
  return transformed;
};